/** @jsx jsx */

import React, {
  useState,
  useRef,
  useContext,
  useEffect,
  useLayoutEffect,
} from 'react';
import { css as _css } from 'emotion';
import { css, jsx } from '@emotion/core';
import { colors, getButtonFocus } from 'styles/variables';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import getBlockById from 'services/apiHelpers/getBlockById';
import { interactionContext } from './interactionContext';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import isIE from 'services/isIE';
import isiOS from 'services/isiOS';
import { useSelector } from 'react-redux';
import getTopics from 'services/apiHelpers/getTopics';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import TopicPanelDefaultIcon from 'assets/TopicPanelDefaultIcon.png';
import hexToRGB from 'services/hexToRGB';
import getCurrentTopic from 'services/apiHelpers/getCurrentTopic';
import setCurrentTopic from 'services/apiHelpers/setCurrentTopic';
import pubSub from 'services/pubSub';
import TopicIcon from './TopicIcon';
import getTopicSId from 'services/getTopicSId';

const { lGrey0, lPink } = colors;

const ProgressBar = ({ color, percentComplete }) => {
  const width = 130;
  const height = 8;

  const { r, g, b } = hexToRGB(color);

  return (
    <div
      css={css`
        position: relative;
        width: ${width}px;
        height: ${height}px;
        border-radius: 9999px;
        background: rgba(${r}, ${g}, ${b}, 0.3);
        transition: background 0.1s;
      `}
    >
      <div
        css={css`
          position: absolute;
          left: 0;
          top: 0;
          width: ${width * (percentComplete / 100)}px;
          height: ${height}px;
          border-radius: 9999px;
          background: rgb(${r}, ${g}, ${b});
          transition: background 0.1s;
        `}
      />
    </div>
  );
};

const TopicPanel = ({ topic }) => {
  const context: any = useContext(interactionContext);
  const { id: interactionId } = context;
  const {
    graphColor = '#61cece',
    cardColor = '#fff',
    textColor = '#1e2533',
    navigateToTopic = true,
    addImage = true,
    cardWidth = 235,
    cardHeight = 150,
  } = context.props;

  let { font = '' } = context.props;

  const { focusedNodeId, theme, interactionsSeen } = useSelector(
    (state: any) => state
  );

  const [iconSelected, setIconSelected] = useState(null);

  const { id } = topic;
  const { name, topicIcon = {} } = topic.props;

  const isEditing = focusedNodeId === interactionId;

  const cardSize = cardWidth === 235 ? 'small' : 'large';

  let navigationEnabled =
    !isEditing && navigateToTopic && getCurrentTopic().id !== id;

  const navigate = navigationEnabled
    ? () => {
        if (window.dev && window.dev()) {
          //
          // console.log('will not nav');
          return;
        }

        const targetLocation = getTopicSId(topic);

        if (targetLocation) {
          // locationHistory
          // we're setting this in module.tsx onHashChange
          // const currentTopic: any = getCurrentTopic();
          // const locationHistory = `${currentTopic.props.sId ||
          //   currentTopic.id}/${context.props.sId || interactionId}`;

          // window.locationHistory = locationHistory;

          //

          window.location.hash = targetLocation;
          // setCurrentTopic(id)
        }
      }
    : null;

  // nuclear
  if (window.dev()) {
    navigationEnabled = false;
  }

  const interactionsInTopic = getInteractionsInTopic(topic)
    .filter((i: any) => i?.meta?.subType !== 'TopicPortal')
    .map((i: any) => i.id);

  const interactionsInTopicSeen = interactionsInTopic.filter((i: any) => {
    return (
      interactionsSeen
        // this might fail ( ? ). replace with approach above ?
        // .filter((i: any) => i.replace('i', '') !== interactionId)
        .filter((i: any) => {
          // console.log({ i });

          return (
            i &&
            getBlockById(i.replace('i', ''))?.meta?.subType !== 'TopicPortal'
          );
        })
        .map((i: any) => i.replace('i', ''))
        .includes(i)
    );
  });

  let percentComplete = Number(
    (
      (interactionsInTopicSeen.length / interactionsInTopic.length) *
      100
    ).toFixed()
  );

  if (isNaN(percentComplete)) {
    percentComplete = 0;
  }

  // update font : theme > local
  // does the section get a font ?
  font = !font ? theme?.fontFamily : font;
  //

  useEffect(() => {
    const pS = pubSub.subscribe('UPDATE_TOPIC_ICON', $ => {
      if (!$.topic) {
        return;
      }
      $.topic.id !== id && setIconSelected(false);
    });

    return () => pS();
  }, []);

  useEffect(() => {
    iconSelected &&
      pubSub.publish(
        'UPDATE_TOPIC_ICON',
        iconSelected
          ? {
              topic,
            }
          : null
      );
  }, [iconSelected]);

  const TopicNameContainer = ({ children }) => {
    const shared = `
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    left: 0;
    width: 100%;
    `;
    return !!addImage ? (
      <div
        css={css`
 ${shared}
  top: ${cardSize === 'small' ? '40px' : '55px'};
  }
  height: calc(100% - ${cardSize === 'small' ? '95px' : '110px'});
  `}
      >
        {children}
      </div>
    ) : (
      <div
        css={css`
          ${shared}
          top: 20px;

          height: calc(100% - 75px);
        `}
      >
        {children}
      </div>
    );
  };

  return interactionsInTopic.length === 0 ? null : (
    <div
      css={css`
        position: relative;
        width: ${cardWidth}px;
        margin: 20px 15px;
        ${navigationEnabled &&
          `
        cursor: pointer;
        transition: opacity 0.15s;

        &:hover {
          opacity: 0.8;
        }`}
      `}
      onClick={() => {
        navigate && navigate();
      }}
      onKeyUp={e => {
        if (![' ', 'Enter'].includes(e.key)) {
          return;
        }

        navigate && navigate();
      }}
      // tabIndex={0}
    >
      <div
        css={css`
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;

          height: ${cardHeight}px;
          width: inherit;

          ${!!addImage &&
            `margin: ${cardSize === 'small' ? '30px' : '45px'} 0 0 0;`}
          border: rgba(0, 0, 0, 0.1);
          border-radius: 40px;
          background: ${cardColor};
          box-shadow: 0 20px 60px -25px rgba(0, 0, 0, 0.4);

          transition: background 0.15s;

          &:focus {
            ${getButtonFocus(
              cardColor !== '#fff' ? cardColor : '#1e2533',
              '0 20px 60px -25px rgba(0, 0, 0, 0.4)'
            )}
          }
        `}
        tabIndex={0}
      >
        <TopicNameContainer>
          <div
            css={css`
              // margin: 0 0 15px 0;
              padding: 5px 15px;
              font-size: 16px;
              font-family: ${font};
              color: ${textColor};
              text-align: center;
            `}
          >
            {name}
          </div>
        </TopicNameContainer>

        <div
          css={css`
            position: absolute;
            display: flex;
            flex-direction: column;
            align-items: center;
            left: 0;
            bottom: 0;
            width: 100%;
            height: ${cardSize === 'small' ? '55px' : '55px'};
          `}
        >
          <div
            css={css`
              margin: 0 0 10px 0;
              font-size: 14px;
              color: ${graphColor};
            `}
          >
            {percentComplete}%
          </div>
          <ProgressBar color={graphColor} percentComplete={percentComplete} />
        </div>
      </div>

      {!!addImage && (
        <TopicIcon
          topicIcon={topicIcon}
          size={cardWidth === 235 ? 60 : 90}
          isEditing={isEditing}
          iconSelected={iconSelected}
          setIconSelected={setIconSelected}
        />
      )}
    </div>
  );
};

export default TopicPanel;
